<template>
  <div class="cg-graficos">
    <!-- Cobertura diaria --------------------------------------------------->
    <div class="card">
      <div class="card-header">
        <h4 class="card-title">Cobertura diaria</h4>
        <p class="card-category">
          % del día con reporte — cuanto más abajo, peor la cobertura
        </p>
      </div>
      <div class="card-body">
        <svg class="cg-chart" :viewBox="`0 0 ${W} ${H_UP}`">
          <!-- tramos sin datos: no se supone señal ahí -->
          <rect
            v-for="(b, i) in bandasSinDatos"
            :key="`band-${i}`"
            :x="b.x"
            :y="MT"
            :width="b.w"
            :height="H_UP - MT - MB"
            fill="rgba(154,160,174,.18)"
          />
          <g v-for="v in [0, 25, 50, 75, 100]" :key="`grid-${v}`">
            <line class="cg-grid" :x1="ML" :y1="yUp(v)" :x2="W - MR" :y2="yUp(v)" />
            <text class="cg-axv" :x="ML - 8" :y="yUp(v) + 3" text-anchor="end">{{ v }}</text>
          </g>
          <text
            v-for="(t, i) in ticksTiempo"
            :key="`tx-${i}`"
            class="cg-ax"
            :x="t.x"
            :y="H_UP - 8"
            text-anchor="middle"
          >{{ t.label }}</text>
          <path
            v-for="s in seriesCobertura"
            :key="`path-${s.uid}`"
            :d="s.d"
            fill="none"
            :stroke="s.hex"
            stroke-width="2"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
          <g v-for="s in seriesCobertura" :key="`pts-${s.uid}`">
            <circle
              v-for="(p, i) in s.puntos"
              :key="i"
              :cx="p.cx"
              :cy="p.cy"
              r="4"
              :fill="s.hex"
              stroke="#fff"
              stroke-width="1.5"
              class="cg-hit"
              @mousemove="tip($event, `${s.alias}<br><b>${p.txt}</b> del ${p.fecha}`)"
              @mouseleave="ocultarTip"
            />
          </g>
        </svg>
        <leyenda-unidades :unidades="unidades" />
      </div>
    </div>

    <!-- Línea de tiempo de cortes ------------------------------------------>
    <div class="card">
      <div class="card-header">
        <h4 class="card-title">Línea de tiempo de cortes</h4>
        <p class="card-category">Cada barra es un corte · el ancho es la duración</p>
      </div>
      <div class="card-body">
        <svg class="cg-chart" :viewBox="`0 0 ${W} ${altoTimeline}`">
          <g v-for="(t, i) in ticksTiempo" :key="`tl-grid-${i}`">
            <line class="cg-grid" :x1="t.x" :y1="MT" :x2="t.x" :y2="altoTimeline - MB" />
            <text class="cg-ax" :x="t.x" :y="altoTimeline - 8" text-anchor="middle">{{ t.label }}</text>
          </g>
          <g v-for="carril in carriles" :key="`lane-${carril.uid}`">
            <rect
              :x="ML"
              :y="carril.y"
              :width="W - ML - MR"
              :height="LANE_H"
              fill="rgba(0,0,0,.03)"
              rx="4"
            />
            <text class="cg-lane" :x="ML + 6" :y="carril.y + 13">{{ carril.label }}</text>
            <rect
              v-for="(c, i) in carril.cortes"
              :key="i"
              :x="c.x"
              :y="carril.y + 16"
              :width="c.w"
              :height="LANE_H - 20"
              rx="3"
              :fill="carril.hex"
              opacity=".92"
              class="cg-hit"
              @mousemove="tip($event, c.tip)"
              @mouseleave="ocultarTip"
            />
          </g>
        </svg>
        <leyenda-unidades :unidades="unidades" />
      </div>
    </div>

    <!-- Duración y hora del día -------------------------------------------->
    <div class="row">
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Duración de los cortes</h4>
            <p class="card-category">Cantidad de cortes por rango de duración</p>
          </div>
          <div class="card-body">
            <grafico-barras :grupo="grupoDuracion" unidad="cortes" @tip="tip" @untip="ocultarTip" />
            <leyenda-unidades :unidades="unidades" />
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Cortes por hora del día</h4>
            <p class="card-category">¿A qué hora pierde señal? — hora en que empezó el corte</p>
          </div>
          <div class="card-body">
            <grafico-barras :grupo="grupoHora" unidad="cortes" denso @tip="tip" @untip="ocultarTip" />
            <leyenda-unidades :unidades="unidades" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="tooltip.visible" class="cg-tooltip" :style="tooltip.estilo" v-html="tooltip.html"></div>
  </div>
</template>

<script>
import {
  DAY,
  BINS,
  coberturaDiaria,
  cortesFiltrados,
  fmtDate,
  fmtDT,
  fmtDur,
  histograma,
  pct,
  porHora,
} from "./cobertura";
import GraficoBarras from "./GraficoBarras.vue";
import LeyendaUnidades from "./LeyendaUnidades.vue";

export default {
  name: "GraficosCobertura",
  components: { GraficoBarras, LeyendaUnidades },
  props: {
    unidades: { type: Array, required: true },
    minFiltro: { type: Number, default: 20 },
  },
  data() {
    return {
      W: 900,
      H_UP: 260,
      ML: 42,
      MR: 16,
      MT: 14,
      MB: 26,
      LANE_H: 34,
      LANE_GAP: 14,
      tooltip: { visible: false, html: "", estilo: {} },
    };
  },
  computed: {
    t0() {
      return Math.min(...this.unidades.map((u) => u.t0));
    },
    t1() {
      return Math.max(...this.unidades.map((u) => u.t1));
    },
    altoTimeline() {
      const n = this.unidades.length;
      return this.MT + this.MB + n * this.LANE_H + (n - 1) * this.LANE_GAP;
    },
    /** Un tick cada 1, 2, 7 o 14 días según el largo del período. */
    ticksTiempo() {
      const span = this.t1 - this.t0;
      const step =
        span > 60 * DAY ? 14 * DAY : span > 20 * DAY ? 7 * DAY : span > 6 * DAY ? 2 * DAY : DAY;
      const out = [];
      for (let t = Math.ceil(this.t0 / DAY) * DAY; t <= this.t1; t += step) {
        out.push({ x: this.x(t), label: fmtDate(new Date(t)) });
      }
      return out;
    },
    bandasSinDatos() {
      const out = [];
      this.unidades.forEach((u) => {
        (u.sinDatos || [])
          .filter((g) => g.tipo === "sin_datos")
          .forEach((g) => {
            const x0 = this.x(Math.max(g.t0, this.t0));
            const x1 = this.x(Math.min(g.t1, this.t1));
            if (x1 > x0 + 0.5) out.push({ x: x0, w: x1 - x0 });
          });
      });
      return out;
    },
    seriesCobertura() {
      return this.unidades.map((u) => {
        const puntos = coberturaDiaria(u, this.minFiltro)
          .map((p) => ({
            ...p,
            cx: this.x(p.t + DAY / 2),
            cy: p.up == null ? null : this.yUp(p.up),
          }))
          .filter((p) => p.cy != null)
          .map((p) => ({
            ...p,
            txt: pct(p.up),
            fecha: fmtDate(new Date(p.t)) + "/" + new Date(p.t).getFullYear(),
          }));

        let d = "";
        let dibujando = false;
        puntos.forEach((p) => {
          d += (dibujando ? " L" : " M") + p.cx.toFixed(1) + " " + p.cy.toFixed(1);
          dibujando = true;
        });

        return { uid: u.uid, hex: u.hex, alias: u.alias, d: d.trim(), puntos };
      });
    },
    carriles() {
      return this.unidades.map((u, i) => ({
        uid: u.uid,
        hex: u.hex,
        label: u.alias.toUpperCase().slice(0, 24),
        y: this.MT + i * (this.LANE_H + this.LANE_GAP),
        cortes: cortesFiltrados(u, this.minFiltro).map((c) => {
          const x0 = this.x(Math.max(c.t0, this.t0));
          const x1 = Math.max(this.x(Math.min(c.t1, this.t1)), x0 + 2);
          return {
            x: x0,
            w: x1 - x0,
            tip:
              `${u.alias}<br>Inició <b>${fmtDT(c.ini)}</b>` +
              `<br>Duró <b>${fmtDur(c.dur)}</b>${c.estado === "en curso" ? " · en curso" : ""}`,
          };
        }),
      }));
    },
    grupoDuracion() {
      return {
        categorias: BINS.map((b) => b[2]),
        series: this.unidades.map((u) => ({
          alias: u.alias,
          hex: u.hex,
          valores: histograma(u, this.minFiltro),
        })),
      };
    },
    grupoHora() {
      return {
        categorias: [...Array(24).keys()].map((h) => String(h).padStart(2, "0")),
        series: this.unidades.map((u) => ({
          alias: u.alias,
          hex: u.hex,
          valores: porHora(u, this.minFiltro),
        })),
      };
    },
  },
  methods: {
    x(t) {
      const ancho = this.W - this.ML - this.MR;
      const r = this.t1 > this.t0 ? (t - this.t0) / (this.t1 - this.t0) : 0;
      return this.ML + r * ancho;
    },
    yUp(v) {
      const alto = this.H_UP - this.MT - this.MB;
      return this.MT + (1 - v / 100) * alto;
    },
    tip(ev, html) {
      this.tooltip = {
        visible: true,
        html,
        estilo: { left: ev.clientX + 14 + "px", top: ev.clientY + 14 + "px" },
      };
    },
    ocultarTip() {
      this.tooltip.visible = false;
    },
  },
};
</script>

<style scoped>
.cg-chart {
  width: 100%;
  height: auto;
  display: block;
  overflow: visible;
}
.cg-grid {
  stroke: var(--cg-line);
  stroke-width: 1;
}
.cg-ax,
.cg-axv {
  font-family: var(--cg-font-mono);
  font-size: 9px;
  fill: var(--cg-muted);
}
.cg-ax {
  letter-spacing: 0.04em;
}
.cg-lane {
  font-family: var(--cg-font-mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  fill: var(--cg-muted);
}
.cg-hit {
  cursor: pointer;
}
.cg-tooltip {
  position: fixed;
  pointer-events: none;
  z-index: 1300;
  background: var(--mq-navy-900);
  color: var(--mq-bone-50);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.45;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  max-width: 260px;
}
</style>
